import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
} from "@repo/ui";
import {
  Megaphone,
  X,
  Sparkles,
  Wrench,
  ShieldAlert,
  Lightbulb,
  ExternalLink,
} from "lucide-react";
import ReactMarkdown from "react-markdown";

import {
  useAnnouncements,
  useDismissAnnouncement,
} from "@/lib/queries/announcements";
import type { Announcement } from "@/lib/api/announcements";

const TYPE_CONFIG: Record<
  Announcement["type"],
  { label: string; icon: typeof Sparkles; badgeClass: string }
> = {
  feature: {
    label: "New Feature",
    icon: Sparkles,
    badgeClass: "bg-primary-soft text-primary border-primary/20",
  },
  maintenance: {
    label: "Maintenance",
    icon: Wrench,
    badgeClass: "bg-warning-soft text-warning border-warning/20",
  },
  compliance: {
    label: "Compliance",
    icon: ShieldAlert,
    badgeClass: "bg-danger-soft text-danger border-danger/20",
  },
  tip: {
    label: "Tip",
    icon: Lightbulb,
    badgeClass: "bg-secondary-soft text-secondary border-secondary/20",
  },
};

const MAX_DISPLAY = 5;

function getRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

interface AnnouncementItemProps {
  announcement: Announcement;
  onDismiss: (id: string) => void;
}

function AnnouncementItem({ announcement, onDismiss }: AnnouncementItemProps) {
  const [expanded, setExpanded] = useState(false);
  const config = TYPE_CONFIG[announcement.type];
  const Icon = config.icon;
  const timeAgo = getRelativeTime(announcement.publishAt);

  return (
    <div className="group rounded-lg px-3 py-2.5 transition-colors hover:bg-muted/40">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex items-center gap-2">
            <Badge
              variant="outline"
              className={`h-5 shrink-0 gap-1 px-1.5 text-[11px] ${config.badgeClass}`}
            >
              <Icon className="h-3 w-3" />
              {config.label}
            </Badge>
            <span className="shrink-0 text-[11px] text-muted-foreground">
              {timeAgo}
            </span>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full text-left"
          >
            <p className="text-sm font-medium leading-tight">
              {announcement.title}
            </p>
            {!expanded && (
              <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                {announcement.body.replace(/[#*_`[\]]/g, "").slice(0, 120)}
              </p>
            )}
          </button>
          {expanded && (
            <div className="prose prose-sm mt-2 max-w-none text-sm text-foreground">
              <ReactMarkdown
                components={{
                  a: ({ children, href }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {children}
                    </a>
                  ),
                  img: ({ src, alt }) => (
                    <img
                      src={src}
                      alt={alt || ""}
                      className="max-w-full rounded-md"
                      loading="lazy"
                    />
                  ),
                }}
                disallowedElements={["script", "iframe", "object", "embed"]}
              >
                {announcement.body}
              </ReactMarkdown>
              {announcement.ctaUrl && (
                <a
                  href={announcement.ctaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                >
                  {announcement.ctaLabel || "Learn more"}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 text-muted-foreground hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(announcement.id);
          }}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function DashboardAnnouncements() {
  const { data: announcements, isLoading } = useAnnouncements();
  const dismissMutation = useDismissAnnouncement();

  if (isLoading || !announcements || announcements.length === 0) {
    return null;
  }

  const displayed = announcements.slice(0, MAX_DISPLAY);
  const remaining = announcements.length - displayed.length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <Megaphone className="h-4 w-4 text-muted-foreground" />
          What&apos;s New
          <Badge variant="secondary" className="h-5 px-1.5 text-[11px]">
            {announcements.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-0.5">
        {displayed.map((announcement) => (
          <AnnouncementItem
            key={announcement.id}
            announcement={announcement}
            onDismiss={(id) => dismissMutation.mutate(id)}
          />
        ))}
        {remaining > 0 && (
          <p className="pt-2 text-center text-xs text-muted-foreground">
            +{remaining} more
          </p>
        )}
      </CardContent>
    </Card>
  );
}
